class DynamicDates extends HTMLElement {
  connectedCallback() {
    // Use requestAnimationFrame to ensure DOM is fully ready
    requestAnimationFrame(() => {
      this.processDates();
    });
  }

  processDates() {
    const dateFormat = (this.getAttribute('data-date-format') || 'dd_mm').trim();
    const dayLabels = this.parseLabels(this.getAttribute('data-day-labels') || '');
    const monthLabels = this.parseLabels(this.getAttribute('data-month-labels') || '');

    const dateElements = this.querySelectorAll('[data-dynamic-date="true"]');
    
    dateElements.forEach(element => {
      const text = element.getAttribute('data-text');
      const minDaysAttr = element.getAttribute('data-min-days');
      const maxDaysAttr = element.getAttribute('data-max-days');
      
      // Skip if no text
      if (!text) {
        return;
      }

      // Parse days - 0 is a valid value meaning "today"
      // null means the attribute wasn't set or is invalid
      let minDays = null;
      let maxDays = null;
      
      // Check if text contains placeholders
      const hasStartDate = text.includes('[start_date]');
      const hasEndDate = text.includes('[end_date]');
      
      // Parse min_days - handle "0" as a valid value, trim whitespace
      if (minDaysAttr !== null) {
        const trimmed = minDaysAttr.trim();
        if (trimmed !== '') {
          const parsed = parseInt(trimmed, 10);
          if (!isNaN(parsed)) {
            minDays = parsed; // This includes 0, which means "today"
          }
        } else if (hasStartDate) {
          // If attribute is empty but placeholder exists, default to 0 (today)
          minDays = 0;
        }
      } else if (hasStartDate) {
        // If attribute doesn't exist but placeholder is in text, default to 0
        minDays = 0;
      }
      
      // Parse max_days - handle "0" as a valid value, trim whitespace
      if (maxDaysAttr !== null) {
        const trimmed = maxDaysAttr.trim();
        if (trimmed !== '') {
          const parsed = parseInt(trimmed, 10);
          if (!isNaN(parsed)) {
            maxDays = parsed; // This includes 0, which means "today"
          }
        } else if (hasEndDate) {
          // If attribute is empty but placeholder exists, default to 0 (today)
          maxDays = 0;
        }
      } else if (hasEndDate) {
        // If attribute doesn't exist but placeholder is in text, default to 0
        maxDays = 0;
      }

      // Calculate dates - 0 means today, null means no date specified
      const startDate = minDays !== null ? this.calculateDate(minDays) : null;
      const endDate = maxDays !== null ? this.calculateDate(maxDays) : null;

      const formattedStartDate = startDate ? this.formatDate(startDate, dateFormat, dayLabels, monthLabels) : '-';
      const formattedEndDate = endDate ? this.formatDate(endDate, dateFormat, dayLabels, monthLabels) : '-';

      let processedText = text
        .replace(/\[start_date\]/g, formattedStartDate)
        .replace(/\[end_date\]/g, formattedEndDate);

      // Update the element's innerHTML to preserve HTML tags
      element.innerHTML = processedText;
    });
  }

  calculateDate(days) {
    const today = new Date();
    const date = new Date(today);
    date.setDate(today.getDate() + days);
    return date;
  }

  formatDate(date, format, dayLabels, monthLabels) {
    if (!date || !format) return '-';
    
    const day = date.getDate();
    const month = date.getMonth();
    const year = date.getFullYear();
    const dayOfWeek = date.getDay();

    // Parse day and month labels
    const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    const defaultMonthNames = ['January', 'February', 'March', 'April', 'May', 'June', 
                              'July', 'August', 'September', 'October', 'November', 'December'];
    
    const dayName = dayLabels.length > dayOfWeek && dayLabels[dayOfWeek] 
      ? dayLabels[dayOfWeek] 
      : dayNames[dayOfWeek];
    
    const monthName = monthLabels.length > month && monthLabels[month]
      ? monthLabels[month]
      : defaultMonthNames[month];

    // Get ordinal suffix for day (1st, 2nd, 3rd, etc.)
    const getOrdinalSuffix = (num) => {
      const j = num % 10;
      const k = num % 100;
      if (j === 1 && k !== 11) return 'st';
      if (j === 2 && k !== 12) return 'nd';
      if (j === 3 && k !== 13) return 'rd';
      return 'th';
    };

    // Normalize format string (trim and lowercase for comparison)
    const normalizedFormat = format.trim().toLowerCase();

    // Format based on the format string
    switch(normalizedFormat) {
      case 'day_mm_dd':
        return `${dayName}, ${monthName} ${day}${getOrdinalSuffix(day)}`;
      
      case 'day_dd_mm':
        return `${dayName}, ${day}. ${monthName}`;
      
      case 'mm_dd':
        return `${monthName} ${day}${getOrdinalSuffix(day)}`;
      
      case 'dd_mm':
        return `${day}. ${monthName}`;
      
      case 'day_dd_mm_numeric':
        return `${dayName}, ${String(day).padStart(2, '0')}. ${String(month + 1).padStart(2, '0')}.`;
      
      case 'dd_mm_numeric':
        return `${String(day).padStart(2, '0')}. ${String(month + 1).padStart(2, '0')}.`;
      
      default:
        // Fallback: try to parse as a template format
        let formatted = format;
        formatted = formatted.replace(/\bday\b/g, dayName);
        formatted = formatted.replace(/\bmm\b/g, monthName);
        formatted = formatted.replace(/\bdd\b/g, String(day));
        formatted = formatted.replace(/\bMM\b/g, String(month + 1).padStart(2, '0'));
        formatted = formatted.replace(/\bDD\b/g, String(day).padStart(2, '0'));
        return formatted;
    }
  }

  parseLabels(labelsString) {
    if (!labelsString) return [];
    return labelsString.split(',').map(label => label.trim()).filter(label => label.length > 0);
  }
}

// Register the custom element
if (!customElements.get('dynamic-dates')) {
  customElements.define('dynamic-dates', DynamicDates);
}

// Process existing elements when script loads
function processAllDynamicDates() {
  const dynamicDatesElements = document.querySelectorAll('dynamic-dates');
  dynamicDatesElements.forEach(element => {
    if (element.processDates) {
      element.processDates();
    }
  });
}

// Process immediately if DOM is ready, otherwise wait
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', processAllDynamicDates);
} else {
  // DOM already loaded
  processAllDynamicDates();
}

// Also handle elements that might be added dynamically (e.g., in theme editor)
if (typeof Shopify !== 'undefined' && Shopify.designMode) {
  document.addEventListener('shopify:section:load', (event) => {
    const dynamicDatesElements = event.target.querySelectorAll('dynamic-dates');
    dynamicDatesElements.forEach(element => {
      if (element.processDates) {
        element.processDates();
      }
    });
  });
  
  document.addEventListener('shopify:section:reorder', processAllDynamicDates);
}

